//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

/** @typedef {{ size: number, name: import('@tidal/styles-image').ImageSize }} ImageSizeDetail */

const COLUMN = 110.25;
const BREAKPOINTS = {
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1404,
};

export default {
    props: {
        /**
         * @property {string} [xsmall]
         * @property {string} [small]
         * @property {string} [medium]
         * @property {string} [large]
         * @property {string} [xlarge]
         * @type {import('vue').PropOptions<import('@tidal/styles-image').ImageSources | string>}
         */
        image: {
            type: [Object, String],
            required: true,
        },
        alt: {
            type: String,
            default: null,
        },
        href: {
            type: String,
            default: null,
        },
        lg: {
            type: Number,
            default: null,
        },
        md: {
            type: Number,
            default: null,
        },
        sm: {
            type: Number,
            default: null,
        },
        xs: {
            type: Boolean,
            default: false,
        },
        active: {
            type: Boolean,
            default: true,
        },
        block: {
            type: Boolean,
            default: true,
        },
        fullwidth: {
            type: Boolean,
            default: false,
        },
        transparent: {
            type: Boolean,
            default: false,
        },
        lazyload: {
            type: Boolean,
            default: false,
        },
        crop: {
            type: Boolean,
            default: false,
        },
        aspectRatio: {
            type: Number,
            default: null,
        },
        avgColor: {
            type: String,
            default: null,
        },
        imageclass: {
            type: String,
            default: null,
        },
        height: {
            type: Number,
            default: null,
        },
        width: {
            type: Number,
            default: null,
        },
        /**
         * @property {string} [xsmall=160]
         * @property {string} [small=320]
         * @property {string} [medium=640]
         * @property {string} [large=1280]
         * @property {string} [xlarge=1920]
         * @type {import('vue').PropOptions<import('@tidal/styles-image').ImageSizes>}
         */
        sizes: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            /** @type {{ srcset?: string, sizes?: string }} */
            imageSources: {},
            /** @type {ImageSizeDetail | undefined} */
            largest: undefined,
            /** @type {import('@tidal/styles-image').ImageDefaultSizes} */
            defaultSizes: {
                xsmall: 160,
                small: 320,
                medium: 640,
                large: 1280,
                xlarge: 1920,
            },
        };
    },
    computed: {
        /** @return {string | undefined} */
        largestSrc() {
            if (typeof(this.image) === 'string') {
                return this.image;
            }

            if (this.largest) {
                return this.image[this.largest.name];
            }

            const imageSizesLength = this.imageSizes.length;
            if (imageSizesLength) {
                return this.image[this.imageSizes[imageSizesLength - 1].name];
            }

            return undefined;
        },
        /** @return {string | undefined} */
        src() {
            if (this.lazyload) {
                return `data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${this.width || '1'} ${this.height || '1'}' height='${this.height || '1'}px' width='${this.width || '1'}px'%3E%3C/svg%3E`;
            }
            return this.largestSrc;
        },
        /** @return {string | undefined} */
        noscript() {
            if (!(this.lazyload || !this.active)) {
                return;
            }

            const classnames = ['tidal-image'];
            if (this.imageclass) {
                classnames.push(this.imageclass);
            }
            if (this.block) {
                classnames.push('block');
            }
            if (this.fullwidth) {
                classnames.push('fullwidth');
            }
            if (this.aspectRatio || this.height) {
                classnames.push('position-absolute');
            }
            if (this.height) {
                classnames.push('position-top');
            }

            return `<img src="${this.largestSrc}" alt="${this.alt || ''}" class="${classnames.join(' ')}" />`;
        },
        /** @return {ImageSizeDetail[]} */
        imageSizes() {
            if (typeof(this.image) === 'string') {
                return [];
            }

            /** @type {ImageSizeDetail[]} */
            const arr = [];
            const sizes = /** @type {import('@tidal/styles-image').ImageDefaultSize[]} */(Object.keys(this.defaultSizes));
            for (const size of sizes) {
                if (this.image[size]) {
                    arr.push({
                        size: this.sizes && this.sizes[size] ? this.sizes[size] : this.defaultSizes[size],
                        name: size,
                    });
                }
            }

            if (this.image.original && this.sizes && this.sizes.original <= this.defaultSizes.xlarge) {
                const size = this.sizes.original;
                if ((!this.image.large && size > this.sizes.medium) || (!this.image.xlarge && size > this.sizes.large)) {
                    arr.push({
                        size: size,
                        name: 'original',
                    });
                }
            }

            return arr;
        },
        /** @return {Record<string, string> | null} */
        imageStyle() {
            if (!this.aspectRatio) {
                return null;
            }

            /** @type {Record<string, string>} */
            const styles = { paddingBottom: (1 / this.aspectRatio * 100) + '%' };
            if (this.avgColor) {
                styles.backgroundColor = this.avgColor;
            }

            return styles;
        },
    },
    created() {
        if (typeof(this.image) === 'string' || !this.imageSizes.length || !(this.lg || this.md || this.sm || this.fullwidth)) {
            return;
        }

        // Reverse array
        const reverseSizes = this.imageSizes.slice().reverse();

        // Check sizes we need
        this.largest = this.fullwidth ? reverseSizes[0] : (this.checkSize(this.imageSizes, this.lg ? (COLUMN * this.lg) : BREAKPOINTS.xl) || reverseSizes[0]);

        // Stop if we only need one image size
        if (this.largest.size === this.imageSizes[0].size) {
            return;
        }

        // Set sizes
        this.imageSources = {
            sizes: this.calculateSizes(),
            srcset: this.calculateSrcset(reverseSizes),
        };
    },
    methods: {
        /**
         * @param {ImageSizeDetail[]} imageSizes
         * @param {number} size
         * @return {ImageSizeDetail | undefined}
         */
        checkSize(imageSizes, size) {
            for (let i = 0; i < imageSizes.length; i++) {
                const imageSize = imageSizes[i].size;
                if (size <= imageSize) {
                    if (!this.largest || imageSize > this.largest.size) {
                        this.largest = imageSizes[i];
                    }
                    return imageSizes[i];
                }
            }
        },
        /** @return {string} */
        calculateSizes() {
            if (this.fullwidth) {
                return '100vw';
            }

            let sizes = '';

            // Calculate biggest breakpoints
            if (this.lg && this.lg !== this.md) {
                sizes += '(min-width: ' + BREAKPOINTS.xl + 'px) calc(' + Math.floor(this.lg * COLUMN) + 'px - 1.5rem), ';
                sizes += '(min-width: ' + BREAKPOINTS.lg + 'px) calc(' + this.gridPercentage(this.lg) + 'vw - 3rem), ';
            }

            // Calculate medium breakpoint
            if (this.md && this.md !== this.sm) {
                sizes += '(min-width: ' + BREAKPOINTS.md + 'px) calc(' + this.gridPercentage(this.md) + 'vw - 3rem), ';
            }

            // Calculate smallest breakpoint
            if (this.sm) {
                if (this.xs) {
                    sizes += '(min-width: ' + BREAKPOINTS.sm + 'px) calc(' + this.gridPercentage(this.sm) + 'vw - 1.5rem), calc(100vw - 3rem)';
                } else {
                    sizes += 'calc(' + this.gridPercentage(this.sm) + 'vw - 1.5rem)';
                }
            } else {
                sizes += 'calc(100vw - 3rem)';
            }

            return sizes;
        },
        /**
         * @param {ImageSizeDetail[]} reverseSizes
         * @return {string}
         */
        calculateSrcset(reverseSizes) {
            if (typeof(this.image) === 'string') {
                return '';
            }

            // Remove the larger images we don't need
            let largestIndex = 0;
            for (let i = 0; i < reverseSizes.length; i++) {
                if (this.largest && reverseSizes[i].size === this.largest.size) {
                    largestIndex = i;
                    break;
                }
            }
            if (largestIndex > 0) {
                reverseSizes.splice(0, largestIndex);
            }

            // Create srcset string
            let srcset = '';
            for (let ix = 0; ix < reverseSizes.length; ix++) {
                const obj = reverseSizes[ix];
                srcset += this.image[obj.name] + ' ' + obj.size + 'w';
                if (ix !== reverseSizes.length - 1) {
                    srcset += ', ';
                }
            }

            return srcset;
        },
        /**
         * @param {number} n
         * @return {number}
         */
        gridPercentage(n) {
            return Math.round(100 / (12 / n) * 100) / 100;
        },
    },
};
